/**
 * RoleAssignmentWizard — full-page overlay for batch role assignment.
 *
 * Opened from FacilitatorDashboard. Shows all users and roles side by side.
 * Moderator can manually assign, mark complexity, auto-assign remaining,
 * then approve all at once. Assignments are staged until approval.
 *
 * DESIGN_BRIEF_REGISTRATION_AND_ROLES.md — Section 3.2
 */

import { useState, useEffect, useMemo, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import {
  getAllUsers, assignUserToRole,
  type UserRecord, type SimRunRole,
} from '@/lib/queries'

function cap(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function positionLabel(role: SimRunRole): string {
  const pos = role.positions?.[0] ?? role.position_type ?? ''
  const map: Record<string, string> = {
    head_of_state: 'HoS', military: 'Military', economy: 'Economy',
    diplomat: 'Diplomat', security: 'Security', opposition: 'Opposition',
    military_chief: 'Military', economy_officer: 'Economy',
  }
  return map[pos] ?? cap(pos.replace(/_/g, ' '))
}

type TimeFilter = 'all' | 'week' | '24h' | 'hour'

interface StagedAssignment {
  roleId: string
  userId: string
}

export function RoleAssignmentWizard({
  simId, roles, onClose, onDone,
}: {
  simId: string
  roles: SimRunRole[]
  onClose: () => void
  onDone: () => void
}) {
  const [users, setUsers] = useState<UserRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('all')
  const [roleFilter, setRoleFilter] = useState<'all' | 'unassigned' | 'assigned'>('all')
  const [staged, setStaged] = useState<StagedAssignment[]>([])
  const [selectedUser, setSelectedUser] = useState<string | null>(null)
  const [complexUsers, setComplexUsers] = useState<Set<string>>(new Set())
  const [selectedForSim, setSelectedForSim] = useState<Set<string>>(new Set())
  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(new Set())
  const [saving, setSaving] = useState(false)
  const [draftSaved, setDraftSaved] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load users + their complexity status + filter out users assigned in ANY sim
  useEffect(() => {
    async function load() {
      try {
        const allUsers = await getAllUsers()
        // Only participants (not moderators), registered or active
        const participants = allUsers.filter(
          u => u.system_role === 'participant' && (u.status === 'registered' || u.status === 'active')
        )

        // Find users already assigned to ANY active/pre_start sim (one user = one sim)
        const { data: assignedRoles } = await supabase
          .from('roles')
          .select('user_id, sim_run_id')
          .not('user_id', 'is', 'null')
          .neq('sim_run_id', '00000000-0000-0000-0000-000000000001')
        // Filter: only assignments in active/pre_start/setup sims (not completed/aborted)
        const { data: activeSims } = await supabase
          .from('sim_runs')
          .select('id')
          .in('status', ['active', 'pre_start', 'setup', 'paused', 'processing'])
          .neq('id', '00000000-0000-0000-0000-000000000001')
        const activeSimIds = new Set((activeSims ?? []).map((s: { id: string }) => s.id))
        // Users assigned in OTHER active sims (not this one)
        const assignedElsewhere = new Set(
          (assignedRoles ?? [])
            .filter((r: { user_id: string; sim_run_id: string }) =>
              r.sim_run_id !== simId && activeSimIds.has(r.sim_run_id))
            .map((r: { user_id: string }) => r.user_id)
        )
        const availableParticipants = participants.filter(u => !assignedElsewhere.has(u.id))
        setUsers(availableParticipants)

        // Load complexity flags from DB
        const { data: complexData } = await supabase
          .from('users')
          .select('id')
          .eq('is_complex_ready', true)
        setComplexUsers(new Set((complexData ?? []).map((u: { id: string }) => u.id)))
      } catch { /* graceful */ }
      finally { setLoading(false) }
    }
    load()
  }, [simId])

  // Initialize staged assignments: restore draft if available, else from current DB state
  useEffect(() => {
    const draftKey = `role_draft_${simId}`
    const saved = localStorage.getItem(draftKey)
    if (saved) {
      try {
        const draft = JSON.parse(saved) as { staged: StagedAssignment[], selected: string[] }
        setStaged(draft.staged)
        setSelectedForSim(new Set(draft.selected))
        return
      } catch { /* fall through to DB state */ }
    }
    const existing: StagedAssignment[] = []
    for (const role of humanRoles) {
      if (role.user_id) {
        existing.push({ roleId: role.id, userId: role.user_id })
      }
    }
    setStaged(existing)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Derived data
  const humanRoles = useMemo(() =>
    roles.filter(r => !r.is_ai_operated && (r.status === 'active' || r.status === 'arrested')),
    [roles]
  )

  const stagedMap = useMemo(() => {
    const byRole: Record<string, string> = {}
    const byUser: Record<string, string> = {}
    for (const s of staged) {
      byRole[s.roleId] = s.userId
      byUser[s.userId] = s.roleId
    }
    return { byRole, byUser }
  }, [staged])

  // Filtered users
  const filteredUsers = useMemo(() => {
    let filtered = users
    if (timeFilter !== 'all') {
      const now = Date.now()
      const cutoff = timeFilter === 'hour' ? 3600000
        : timeFilter === '24h' ? 86400000
        : 604800000 // week
      filtered = filtered.filter(u => now - new Date(u.created_at).getTime() < cutoff)
    }
    return filtered
  }, [users, timeFilter])

  // Filtered roles
  const filteredRoles = useMemo(() => {
    if (roleFilter === 'unassigned') return humanRoles.filter(r => !stagedMap.byRole[r.id])
    if (roleFilter === 'assigned') return humanRoles.filter(r => !!stagedMap.byRole[r.id])
    return humanRoles
  }, [humanRoles, roleFilter, stagedMap])

  // Group roles by country
  const rolesByCountry = useMemo(() => {
    const groups: Record<string, SimRunRole[]> = {}
    for (const r of filteredRoles) {
      if (!groups[r.country_code]) groups[r.country_code] = []
      groups[r.country_code].push(r)
    }
    return Object.entries(groups).sort(([a], [b]) => a.localeCompare(b))
  }, [filteredRoles])

  // Handlers
  const handleAssign = useCallback((roleId: string) => {
    if (!selectedUser) return
    // Remove any existing assignment for this user or role
    setStaged(prev => [
      ...prev.filter(s => s.roleId !== roleId && s.userId !== selectedUser),
      { roleId, userId: selectedUser },
    ])
    setSelectedUser(null)
  }, [selectedUser])

  const handleUnassign = useCallback((roleId: string) => {
    setStaged(prev => prev.filter(s => s.roleId !== roleId))
  }, [])

  const handleToggleComplex = useCallback(async (userId: string) => {
    const isNow = complexUsers.has(userId)
    const next = new Set(complexUsers)
    if (isNow) next.delete(userId); else next.add(userId)
    setComplexUsers(next)
    // Save to DB
    await supabase.from('users').update({ is_complex_ready: !isNow }).eq('id', userId)
  }, [complexUsers])

  const handleToggleSelectedForSim = useCallback((userId: string) => {
    setSelectedForSim(prev => {
      const next = new Set(prev)
      if (next.has(userId)) {
        next.delete(userId)
      } else {
        // Don't allow selecting more users than available human roles
        const availableSlots = humanRoles.filter(r => !r.expansion_role).length
        const alreadyAssigned = staged.filter(s => next.has(s.userId)).length
        if (next.size - alreadyAssigned < availableSlots) {
          next.add(userId)
        }
      }
      return next
    })
  }, [humanRoles, staged])

  const handleSelectAll = useCallback(() => {
    const maxSlots = humanRoles.filter(r => !r.expansion_role).length
    if (selectedForSim.size > 0) {
      setSelectedForSim(new Set())
    } else {
      const limited = new Set(users.slice(0, maxSlots).map(u => u.id))
      setSelectedForSim(limited)
    }
  }, [users, humanRoles, selectedForSim])

  const handleToggleRole = useCallback((roleId: string) => {
    setSelectedRoles(prev => {
      const next = new Set(prev)
      if (next.has(roleId)) next.delete(roleId); else next.add(roleId)
      return next
    })
  }, [])

  const handleAutoAssign = useCallback(() => {
    // If no users selected on left side, nothing to do
    const assignedUserIds = new Set(staged.map(s => s.userId))
    const unassignedUsers = users.filter(u => selectedForSim.has(u.id) && !assignedUserIds.has(u.id))
    if (unassignedUsers.length === 0) return

    // If roles selected on right side, only assign within those; otherwise all unassigned
    const targetRoles = selectedRoles.size > 0
      ? humanRoles.filter(r => selectedRoles.has(r.id) && !stagedMap.byRole[r.id])
      : humanRoles.filter(r => !stagedMap.byRole[r.id])
    if (targetRoles.length === 0) return

    // Categorize roles: complex first, then normal, then expansion (optional) last
    const complexRoles = targetRoles.filter(r => r.is_complex_role && !r.expansion_role)
    const normalRoles = targetRoles.filter(r => !r.is_complex_role && !r.expansion_role)
    const expansionRoles = targetRoles.filter(r => r.expansion_role)

    const complexReadyUsers = unassignedUsers.filter(u => complexUsers.has(u.id))
    const otherUsers = unassignedUsers.filter(u => !complexUsers.has(u.id))

    const newAssignments: StagedAssignment[] = [...staged]
    const usedUsers = new Set(assignedUserIds)

    const shuffle = <T,>(arr: T[]): T[] => {
      const a = [...arr]
      for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1))
        ;[a[i], a[j]] = [a[j], a[i]]
      }
      return a
    }

    const assign = (roleList: SimRunRole[], userList: UserRecord[]) => {
      const r = shuffle(roleList)
      const u = shuffle(userList.filter(x => !usedUsers.has(x.id)))
      for (let i = 0; i < Math.min(r.length, u.length); i++) {
        newAssignments.push({ roleId: r[i].id, userId: u[i].id })
        usedUsers.add(u[i].id)
      }
    }

    // Step 1: Complex roles → complex-ready users
    assign(complexRoles, complexReadyUsers)

    // Step 2: Remaining complex roles + normal roles → all remaining users
    const remainingComplexRoles = complexRoles.filter(r => !newAssignments.some(a => a.roleId === r.id))
    const allRemainingUsers = [...complexReadyUsers, ...otherUsers].filter(u => !usedUsers.has(u.id))
    assign([...remainingComplexRoles, ...normalRoles], allRemainingUsers)

    // Step 3: Expansion (optional) roles → any remaining users (assigned LAST)
    const stillAvailable = [...complexReadyUsers, ...otherUsers].filter(u => !usedUsers.has(u.id))
    assign(expansionRoles, stillAvailable)

    setStaged(newAssignments)
    setSelectedRoles(new Set()) // clear role selection after assign
  }, [humanRoles, stagedMap, staged, users, complexUsers, selectedForSim, selectedRoles])

  const handleSaveDraft = useCallback(() => {
    const draftKey = `role_draft_${simId}`
    localStorage.setItem(draftKey, JSON.stringify({
      staged,
      selected: Array.from(selectedForSim),
    }))
    setDraftSaved(true)
    setTimeout(() => setDraftSaved(false), 2000)
  }, [simId, staged, selectedForSim])

  const handleClearAll = useCallback(() => {
    if (!confirm('Clear all role assignments and start over?')) return
    setStaged([])
    localStorage.removeItem(`role_draft_${simId}`)
  }, [simId])

  const handleApprove = async () => {
    setSaving(true)
    setError(null)
    try {
      // Refresh auth token before batch writes (prevents 403 from expired JWT)
      await supabase.auth.refreshSession()

      // Only update roles that CHANGED from current DB state
      for (const role of humanRoles) {
        const stagedUserId = stagedMap.byRole[role.id] ?? null
        const currentUserId = role.user_id ?? null
        if (stagedUserId !== currentUserId) {
          await assignUserToRole(simId, role.id, stagedUserId)
        }
      }
      // Clear draft on successful approve
      localStorage.removeItem(`role_draft_${simId}`)
      onDone()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Assignment failed')
      setSaving(false)
    }
  }

  // Stats
  const assignedCount = staged.length
  const totalHuman = humanRoles.length
  const unassignedCount = totalHuman - assignedCount

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center">
        <p className="font-body text-body-sm text-text-secondary">Loading...</p>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 bg-base flex flex-col">
      {/* Header */}
      <div className="bg-card border-b border-border px-6 py-4 flex items-center justify-between shrink-0">
        <div>
          <h2 className="font-heading text-h2 text-text-primary">Role Assignment</h2>
          <p className="font-body text-caption text-text-secondary mt-1">
            <span className={selectedForSim.size > 0 ? 'text-action font-semibold' : 'text-text-secondary'}>{selectedForSim.size} selected</span>
            <span className="mx-1">/</span>
            <span>{totalHuman} human roles</span>
            <span className="mx-2">·</span>
            {assignedCount}/{totalHuman} assigned
            {unassignedCount > 0 && <span className="text-warning ml-2">{unassignedCount} unassigned</span>}
            <span className="ml-3 text-accent">{'\u2605'} {complexUsers.size} complex-ready</span>
            <span className="ml-2 text-text-secondary/50">{humanRoles.filter(r => r.expansion_role).length} optional</span>
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={handleClearAll}
            className="font-body text-body-sm font-medium text-danger/70 hover:text-danger px-3 py-2 transition-colors"
            title="Clear all assignments and start over">
            Clear All
          </button>
          <button onClick={handleAutoAssign}
            className="font-body text-body-sm font-medium bg-accent/10 text-accent px-4 py-2 rounded-lg hover:bg-accent/20 transition-colors">
            Auto-Assign Selected
          </button>
          <button onClick={handleSaveDraft}
            className="font-body text-body-sm font-medium bg-base border border-border text-text-primary px-4 py-2 rounded-lg hover:bg-card transition-colors">
            {draftSaved ? '✓ Draft Saved' : 'Save Draft'}
          </button>
          <button onClick={handleApprove} disabled={saving}
            className="font-body text-body-sm font-medium bg-action text-white px-6 py-2 rounded-lg hover:opacity-90 disabled:opacity-50 transition-opacity">
            {saving ? 'Saving...' : 'Approve & Reveal'}
          </button>
          <button onClick={() => { handleSaveDraft(); onClose() }}
            className="font-body text-body-sm text-text-secondary hover:text-text-primary px-3 py-2">
            Back to Dashboard
          </button>
        </div>
      </div>

      {error && (
        <div className="mx-6 mt-3 bg-danger/10 border border-danger/30 rounded-lg p-3">
          <p className="font-body text-body-sm text-danger">{error}</p>
        </div>
      )}

      {/* Two-panel layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* LEFT: Users */}
        <div className="w-1/3 border-r border-border flex flex-col">
          <div className="px-4 py-3 border-b border-border bg-card">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-heading text-body-sm text-text-primary">Registered Users</h3>
              <button onClick={handleSelectAll}
                className="font-body text-caption text-action hover:text-action/80 transition-colors">
                {selectedForSim.size > 0 ? 'Clear all' : 'Select all'}
              </button>
            </div>
            <div className="flex gap-1">
              {(['all', 'hour', '24h', 'week'] as TimeFilter[]).map(f => (
                <button key={f} onClick={() => setTimeFilter(f)}
                  className={`font-body text-caption px-2 py-0.5 rounded transition-colors ${
                    timeFilter === f ? 'bg-action/15 text-action' : 'text-text-secondary hover:text-text-primary'
                  }`}>
                  {f === 'all' ? 'All' : f === 'hour' ? '1h' : f === '24h' ? '24h' : '7d'}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-1">
            {filteredUsers.map(u => {
              const assignedRole = stagedMap.byUser[u.id]
              const role = assignedRole ? humanRoles.find(r => r.id === assignedRole) : null
              const isSelected = selectedUser === u.id
              const isComplex = complexUsers.has(u.id)
              const isChecked = selectedForSim.has(u.id)
              const canClick = isChecked && !assignedRole
              return (
                <div key={u.id}
                  onClick={() => canClick && setSelectedUser(isSelected ? null : u.id)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                    isSelected ? 'bg-action/10 border border-action/30 cursor-pointer'
                    : assignedRole ? 'bg-success/5 border border-success/20 opacity-70'
                    : isChecked ? 'hover:bg-base border border-action/10 cursor-pointer'
                    : 'border border-transparent opacity-50'
                  }`}>
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => handleToggleSelectedForSim(u.id)}
                    onClick={(e) => e.stopPropagation()}
                    className="w-4 h-4 shrink-0 accent-action"
                    title="Include in this simulation"
                  />
                  <button onClick={(e) => { e.stopPropagation(); handleToggleComplex(u.id) }}
                    className={`text-sm shrink-0 ${isComplex ? 'text-accent' : 'text-text-secondary/30 hover:text-accent/50'}`}
                    title={isComplex ? 'Complex-ready (click to remove)' : 'Mark as complex-ready'}>
                    {isComplex ? '\u2605' : '\u2606'}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="font-body text-body-sm text-text-primary truncate">{u.display_name}</div>
                    <div className="font-body text-caption text-text-secondary truncate">{u.email}</div>
                  </div>
                  {role && (
                    <span className="font-body text-caption text-success shrink-0">
                      {role.character_name}
                    </span>
                  )}
                </div>
              )
            })}
            {filteredUsers.length === 0 && (
              <p className="font-body text-caption text-text-secondary text-center py-4">No users match filter</p>
            )}
          </div>
        </div>

        {/* RIGHT: Roles */}
        <div className="flex-1 flex flex-col">
          <div className="px-4 py-3 border-b border-border bg-card flex items-center justify-between">
            <h3 className="font-heading text-body-sm text-text-primary">Roles</h3>
            <div className="flex gap-1">
              {(['all', 'unassigned', 'assigned'] as const).map(f => (
                <button key={f} onClick={() => setRoleFilter(f)}
                  className={`font-body text-caption px-2 py-0.5 rounded transition-colors ${
                    roleFilter === f ? 'bg-action/15 text-action' : 'text-text-secondary hover:text-text-primary'
                  }`}>
                  {f === 'all' ? 'All' : f === 'unassigned' ? 'Unassigned' : 'Assigned'}
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-4">
            {selectedUser && (
              <div className="bg-action/5 border border-action/20 rounded-lg px-4 py-2 text-center">
                <span className="font-body text-body-sm text-action">
                  Click a role to assign <strong>{users.find(u => u.id === selectedUser)?.display_name}</strong>
                </span>
              </div>
            )}
            {rolesByCountry.map(([countryCode, countryRoles]) => (
              <div key={countryCode}>
                <h4 className="font-heading text-caption text-text-primary uppercase tracking-wider mb-1.5">
                  {cap(countryCode)}
                </h4>
                <div className="space-y-1">
                  {countryRoles.map(role => {
                    const assignedUserId = stagedMap.byRole[role.id]
                    const assignedUser = assignedUserId ? users.find(u => u.id === assignedUserId) : null
                    const isComplex = role.is_complex_role
                    const isRoleSelected = selectedRoles.has(role.id)
                    return (
                      <div key={role.id}
                        onClick={() => selectedUser && !assignedUserId && handleAssign(role.id)}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                          isRoleSelected ? 'border border-accent/30 bg-accent/5'
                          : selectedUser && !assignedUserId ? 'cursor-pointer hover:bg-action/10 border border-action/20'
                          : assignedUserId ? 'bg-success/5 border border-success/15'
                          : 'border border-border'
                        }`}>
                        <input
                          type="checkbox"
                          checked={isRoleSelected}
                          onChange={() => handleToggleRole(role.id)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-4 h-4 shrink-0 accent-accent"
                          title="Select for auto-assign"
                        />
                        {isComplex && <span className="text-accent text-sm shrink-0" title="Complex role">{'\u2605'}</span>}
                        <div className="flex-1 min-w-0">
                          <span className="font-body text-body-sm text-text-primary font-medium">{role.character_name}</span>
                          <span className="font-body text-caption text-text-secondary ml-2">{positionLabel(role)}</span>
                          {role.expansion_role && <span className="font-body text-[10px] text-text-secondary/50 ml-1">(optional)</span>}
                        </div>
                        <select
                          value={assignedUserId ?? ''}
                          onChange={(e) => {
                            e.stopPropagation()
                            const uid = e.target.value
                            if (!uid) {
                              handleUnassign(role.id)
                            } else {
                              setStaged(prev => [
                                ...prev.filter(s => s.roleId !== role.id && s.userId !== uid),
                                { roleId: role.id, userId: uid },
                              ])
                            }
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="shrink-0 bg-base border border-border rounded px-2 py-1 font-body text-caption text-text-primary max-w-[180px] focus:border-action focus:outline-none"
                        >
                          <option value="">— unassigned —</option>
                          {users
                            .filter(u => selectedForSim.has(u.id))
                            .map(u => {
                              const otherRole = stagedMap.byUser[u.id]
                              const taken = otherRole && otherRole !== role.id
                              return (
                                <option key={u.id} value={u.id} disabled={!!taken}>
                                  {u.display_name}{taken ? ` (→ ${humanRoles.find(r => r.id === otherRole)?.character_name ?? '?'})` : ''}
                                </option>
                              )
                            })}
                        </select>
                      </div>
                    )
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
